import React from "react";
import { createPortal } from "react-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { updateContact } from "@/_actions/contactActions";
import {
  BotMessageSquare,
  PhoneOutgoing,
  MessageSquare,
  Loader2Icon,
  Trash,
  Dot,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { parse, formatRelative } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { es } from "date-fns/locale";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import apiClient from "@/utils/apiClient";
import PhoneValidationBadge, { getWhatsappPhone, getTelPhone } from "../PhoneValidationBadge";

const formatDate = (created) => {
  const parsedDate = parse(created, "dd-MM-yyyy HH:m:s", new Date());
  return formatRelative(parsedDate, new Date(), { locale: es });
};

export const getColumns = (pipelines = [], mutate) => {
  return [
    {
      accessorFn: (row) => formatDate(row.created),
      header: "Fecha",
    },
    {
      accessorKey: "property_title",
      header: "Propiedad",
    },
    {
      accessorKey: "property_id",
      header: "ID de Propiedad",
      cell: (info) => (
        <a
          href={`${import.meta.env.VITE_PORTAL_URL}/propiedad/${info.getValue()}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {info.getValue()}
        </a>
      ),
    },
    {
      accessorKey: "utm_campaign",
      header: "Origen",
      cell: (info) => {
        if (info.row.original.contact_place === "facebook") {
          return <Badge className="bg-blue-600 text-white w-full">Campaña</Badge>;
        }

        const map = {
          Whatsapp_Button: "WhatsApp",
          Call_Button: "Llamada",
          Pro_Crm: "PRO CRM",
        };

        return info.getValue() ? (
          <Badge className="bg-green-800">{map[info.getValue()] || info.getValue()}</Badge>
        ) : (
          <Badge>Formulario</Badge>
        );
      },
    },
    {
      accessorKey: "name",
      header: "Nombre",
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "pipeline_status",
      header: "Estatus",
      cell: (info) => {
        const [loading, setLoading] = React.useState(false);

        const handleSelect = async (selected) => {
          setLoading(true);
          try {
            await apiClient.patch(`contacts/${info.row.original.id}/pipeline-stage/`, {
              stage: selected,
            });
            await mutate();
          } finally {
            setLoading(false);
          }
        };

        return pipelines.length > 0 ? (
          <Select
            value={info.getValue()?.stage?.id || ""}
            onValueChange={handleSelect}
            disabled={loading}
          >
            <SelectTrigger className="w-full">
              {loading ? (
                <div className="flex items-center gap-2">
                  <Loader2Icon className="animate-spin h-4 w-4" />
                  <span>Actualizando...</span>
                </div>
              ) : (
                <SelectValue placeholder="Seleccionar Estatus" />
              )}
            </SelectTrigger>
            <SelectContent>
              {pipelines[0].stages.map((stage) => (
                <SelectItem className="flex " key={stage.id} value={stage.id}>
                  <span className="flex items-center ">
                    <Dot
                      className="h-10 w-10 "
                      style={{
                        color: stage.color || "#ccc",
                      }}
                    />
                    {stage.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge>{info.getValue()?.stage?.name}</Badge>
        );
      },
    },
    {
      accessorKey: "phone",
      cell: (info) => {
        const validation = info.row.original.phone_validation;
        const whatsappPhone = getWhatsappPhone(validation, info.getValue());
        const telPhone = getTelPhone(validation, info.getValue());
        return (
          <>
            {info.getValue() ? (
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-full">
                      {info.getValue()}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <MessageSquare className="mr-2 h-4 w-4 " />
                      <a
                        href={`https://wa.me/${whatsappPhone}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Iniciar conversación en WhatsApp
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <PhoneOutgoing className="mr-2 h-4 w-4 " />
                      <a href={`tel:${telPhone}`} target="_blank" rel="noopener noreferrer">
                        Llamar
                      </a>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <BotMessageSquare className="mr-2 h-4 w-4 " />
                      <span className="text-gray-500">Monitorear con Autopilot (próximamente)</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <PhoneValidationBadge validation={validation} compact />
              </div>
            ) : (
              <span>---</span>
            )}
          </>
        );
      },
      header: "Teléfono",
    },
    {
      cell: (info) => {
        const [open, setOpen] = React.useState(false);
        const [submitting, setSubmitting] = React.useState(false);
        const values = {
          status: 3,
          status_description: "Eliminado por el usuario",
        };
        return (
          <>
            <AlertDialog open={open} onOpenChange={setOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              {createPortal(
                <div onClick={(e) => e.stopPropagation()}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        ¿Estás seguro de que deseas eliminar este contacto?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta acción no se puede deshacer.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={submitting}>Cancelar</AlertDialogCancel>
                      <form
                        onSubmit={(event) => {
                          setSubmitting(true);
                          updateContact(info.row.original.id, values).then(() => setOpen(false));
                          mutate();
                          setSubmitting(false);
                          event.preventDefault();
                        }}
                      >
                        {/** some inputs */}
                        <Button disabled={submitting} type="submit">
                          Continuar
                        </Button>
                      </form>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </div>,
                document.body,
              )}
            </AlertDialog>
          </>
        );
      },
      header: "Acciones",
    },
  ];
};
